/**
 * Entreprises d'un compte personnel (décision du propriétaire du 25 octobre
 * 2026) : une personne, un compte, plusieurs adhésions. Chaque adhésion a son
 * identifiant (`users.id`), sa base hors ligne (`myequip-<userId>`) et ses
 * jetons d'accès ; le jeton de renouvellement est celui du compte.
 *
 * Le navigateur partage la session entre ses onglets (`fa.access`,
 * `fa.refresh`) : `fa.member` dit à quelle adhésion appartiennent ces jetons.
 * Un onglet qui affiche une autre entreprise n'envoie plus rien (`api.ts`) et
 * suit le changement (`auth.tsx`, événement `storage`).
 */

/** Adhésion du compte, telle que `/auth/me` la décrit (sélecteur d'entreprise). */
export interface Membership {
  userId: string;
  companyId: string;
  companyName: string;
  role: string;
  roleName: string | null;
  /** `actif` seulement est ouvrable ; `verrouille`, `suspendu`, `inactif` restent listés. */
  status: string;
  /** Place de comptable tenue fermée jusqu'à la vérification du compte : la fiche l'ouvre. */
  advisorHold?: boolean;
  current: boolean;
}

/** Adhésion à laquelle appartiennent les jetons partagés des onglets. */
export const MEMBER_KEY = 'fa.member';

/** Dernier profil connu, pour un rechargement sans réseau (`auth.tsx`) ; seule source des adhésions hors React. */
export const ME_CACHE = 'fa.me';

export function storedMember(): string | undefined {
  try {
    return localStorage.getItem(MEMBER_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function writeStoredMember(userId: string | undefined): void {
  try {
    if (userId) localStorage.setItem(MEMBER_KEY, userId);
    else localStorage.removeItem(MEMBER_KEY);
  } catch {
    /* sans stockage, un seul onglet : rien à coordonner */
  }
}

/** Adhésions lisibles d'un profil, y compris d'un profil gardé avant leur arrivée (aucune). */
export function membershipsOf(me: { memberships?: unknown } | undefined): Membership[] {
  const list = me?.memberships;
  if (!Array.isArray(list)) return [];
  return list.filter((item): item is Membership => Boolean(item) && typeof item === 'object'
    && typeof (item as Membership).userId === 'string' && typeof (item as Membership).companyName === 'string');
}

/**
 * Autres adhésions en service du dernier profil connu, dans son ordre : le
 * renouvellement s'y reporte quand l'adhésion renouvelée a été retirée, plutôt
 * que de déconnecter la personne de toutes ses entreprises.
 */
export function fallbackMembers(): string[] {
  let cached: unknown;
  try {
    cached = JSON.parse(localStorage.getItem(ME_CACHE) ?? 'null');
  } catch {
    return [];
  }
  if (!cached || typeof cached !== 'object') return [];
  const current = (cached as { id?: unknown }).id;
  return membershipsOf(cached as { memberships?: unknown })
    .filter((m) => m.status === 'actif' && m.userId !== current)
    .map((m) => m.userId)
    .slice(0, 5);
}

// --- Invitation acceptée avec un compte existant -------------------------------

/**
 * Invitation que la personne a choisi d'accepter avec son compte existant, le
 * temps de se connecter : la fin de connexion la ramène à la page de
 * l'invitation (onglet seulement, effacée dès qu'elle a servi).
 */
const PENDING_INVITATION = 'myequip.invitation';

export interface PendingInvitation {
  token: string;
  companyName: string;
}

export function readPendingInvitation(): PendingInvitation | undefined {
  try {
    const value = JSON.parse(sessionStorage.getItem(PENDING_INVITATION) ?? 'null') as PendingInvitation | null;
    return value && typeof value.token === 'string' && typeof value.companyName === 'string' ? value : undefined;
  } catch {
    return undefined;
  }
}

export function writePendingInvitation(value: PendingInvitation | undefined): void {
  try {
    if (value) sessionStorage.setItem(PENDING_INVITATION, JSON.stringify(value));
    else sessionStorage.removeItem(PENDING_INVITATION);
  } catch {
    /* sans stockage : la personne rouvre le lien de l'invitation après s'être connectée */
  }
}

/** Page d'une invitation, à rouvrir après la connexion. */
export function invitationPath(token: string): string {
  return `/invitation?token=${encodeURIComponent(token)}`;
}
